import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_MY_CART, UPDATE_CART_ITEM, REMOVE_FROM_CART, CREATE_QUOTATION } from '../../apollo/queries';
import { useLoading } from '../../contexts/LoadingContext';
import { useAuth } from '../../admin/AuthContext';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';
import LoginPromptModal from '../../components/LoginPromptModal';
import { uploadFileToS3 } from '../../utils/s3Upload';
import '../css/CartPage.css';

const CartPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [step, setStep] = useState(1);
    const [additionalRequest, setAdditionalRequest] = useState('');
    const [cartItems, setCartItems] = useState([]);
    const [quotationNumber, setQuotationNumber] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const { startLoading, stopLoading, updateMessage } = useLoading();

    // GraphQL 쿼리 및 뮤테이션 - 모든 Hooks를 먼저 호출
    const { data, loading, error, refetch } = useQuery(GET_MY_CART, {
        errorPolicy: 'all',
        skip: !isAuthenticated() // 로그인된 사용자만 쿼리 실행
    });

    const [updateCartItem] = useMutation(UPDATE_CART_ITEM, {
        onCompleted: () => {
            refetch();
        },
        onError: (error) => {
            console.error('장바구니 수정 에러:', error);
            alert('장바구니 수정 중 오류가 발생했습니다.');
        }
    });

    const [removeFromCart] = useMutation(REMOVE_FROM_CART, {
        onCompleted: () => {
            refetch();
        },
        onError: (error) => {
            console.error('장바구니 삭제 에러:', error);
            alert('장바구니 삭제 중 오류가 발생했습니다.');
        }
    });

    const [createQuotation, { loading: creatingQuotation }] = useMutation(CREATE_QUOTATION, {
        onCompleted: (data) => {
            const quotation = data.createQuotation;
            setQuotationNumber(`QT-${quotation.id.toString().padStart(6, '0')}`);
            setStep(3);
        },
        onError: (error) => {
            console.error('견적 요청 에러:', error);
            alert('견적 요청 중 오류가 발생했습니다.');
        }
    });

    // 로딩 상태 관리
    useEffect(() => {
        if (loading) {
            startLoading('장바구니를 불러오는 중...');
        } else if (creatingQuotation) {
            updateMessage('견적 요청을 처리하는 중...');
        } else {
            stopLoading();
        }
    }, [loading, creatingQuotation, startLoading, stopLoading, updateMessage]);

    // 컴포넌트 마운트 시 로그인 체크
    useEffect(() => {
        if (!isAuthenticated()) {
            setShowLoginPrompt(true);
        }
    }, [isAuthenticated]);

    // 장바구니 데이터 변환
    useEffect(() => {
        if (data?.myCart) {
            const transformedItems = data.myCart.map(item => {
                // 안전한 이미지 URL 추출
                let imgSrc = null;
                
                try {
                    if (item.product?.images) {
                        if (typeof item.product.images === 'string') {
                            const cleanedImages = item.product.images.trim();
                            
                            // 빈 문자열이나 빈 배열 체크
                            if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                                if (cleanedImages.startsWith('[') || cleanedImages.startsWith('{')) {
                                    const parsedImages = JSON.parse(cleanedImages);
                                    if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                                        imgSrc = parsedImages[0];
                                    }
                                } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                                    // 단일 URL 문자열인 경우
                                    imgSrc = cleanedImages;
                                }
                            }
                        } else if (Array.isArray(item.product.images)) {
                            imgSrc = item.product.images.length > 0 ? item.product.images[0] : null;
                        }
                    }
                } catch (error) {
                    console.error('이미지 파싱 에러:', error);
                    imgSrc = null;
                }

                return {
                    id: item.id,
                    productId: item.productId,
                    quantity: item.quantity,
                    notes: item.notes || '',
                    imgSrc: imgSrc,
                    name: item.product?.name || '제품명 없음',
                    code: item.product?.productCode || item.product?.id || '코드 없음',
                    size: item.product?.size || '미입력',
                    options: item.product?.options || [],
                    product: item.product // 원본 product 객체 유지
                };
            });
            setCartItems(transformedItems);
        }
    }, [data]);

    // 장바구니 아이템을 견적서 형식으로 변환
    useEffect(() => {
        if (cartItems && cartItems.length > 0) {
            const transformedItems = cartItems.map(item => ({
                id: item.id,
                product: {
                    ...item.product,
                    // 사이즈 필드 처리 수정
                    size: (item.product?.size && item.product.size.trim() !== '') ? item.product.size : '미입력',
                    images: item.product?.images
                },
                quantity: item.quantity,
                notes: item.notes || '',
                estimatedPrice: item.product?.discountPrice || item.product?.price || 0
            }));
            // setQuotationItems(transformedItems); // This line was removed from the original file, so it's removed here.
        }
    }, [cartItems]);

    const handleQuantityChange = (id, amount) => {
        const item = cartItems.find(item => item.id === id);
        if (item) {
            const newQuantity = Math.max(1, item.quantity + amount);
            updateCartItem({
                variables: {
                    id: id,
                    quantity: newQuantity
                }
            });
        }
    };

    const handleCheckItem = (id) => {
        setCartItems(cartItems.map(item =>
            item.id === id ? { ...item, checked: !item.checked } : item
        ));
    };

    const handleCheckAll = (e) => {
        const { checked } = e.target;
        setCartItems(cartItems.map(item => ({ ...item, checked })));
    };

    const deleteItem = (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            removeFromCart({
                variables: { id: id }
            });
        }
    };

    const deleteSelected = () => {
        const selectedItems = cartItems.filter(item => item.checked);
        if (selectedItems.length === 0) {
            alert('삭제할 항목을 선택해주세요.');
            return;
        }
        
        if (window.confirm(`선택된 ${selectedItems.length}개 항목을 삭제하시겠습니까?`)) {
            selectedItems.forEach(item => {
                removeFromCart({
                    variables: { id: item.id }
                });
            });
        }
    };
    
    const handleNextStep = () => {
        const selectedItems = cartItems.filter(item => item.checked);
        if (selectedItems.length === 0) {
            alert('견적을 요청할 상품을 선택해주세요.');
            return;
        }
        setStep(step + 1);
    };

    const handlePrevStep = () => {
        setStep(step - 1);
    };

    // 파일 업로드 핸들러
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const uploadPromises = files.map(async (file) => {
                const uploadedFileUrl = await uploadFileToS3(file);
                return {
                    name: file.name,
                    url: uploadedFileUrl,
                    size: file.size,
                    type: file.type
                };
            });

            const uploadedFileInfos = await Promise.all(uploadPromises);
            setUploadedFiles(prev => [...prev, ...uploadedFileInfos]);
        } catch (error) {
            console.error('파일 업로드 에러:', error);
            alert('파일 업로드 중 오류가 발생했습니다.');
        } finally {
            setIsUploading(false);
        }
    };

    // 파일 삭제 핸들러
    const handleRemoveFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreateQuotation = () => {
        // 장바구니가 비어있는 경우 체크
        if (!cartItems || cartItems.length === 0) {
            alert('장바구니에 상품이 없습니다.');
            return;
        }

        // 체크된 상품들 필터링
        const selectedItems = cartItems.filter(item => item.checked);
        if (selectedItems.length === 0) {
            alert('견적을 요청할 상품을 선택해주세요.');
            return;
        }

        // 견적 요청 아이템 생성 - productId와 quantity 확인
        const quotationItems = selectedItems.map(item => {
            const productId = item.productId || item.product?.id;
            if (!productId) {
                console.error('Product ID missing for item:', item);
                return null;
            }
            return {
                productId: productId,
                quantity: item.quantity || 1
            };
        }).filter(item => item.productId); // productId가 있는 것만 필터링

        if (quotationItems.length === 0) {
            alert('유효한 상품 정보가 없습니다.');
            return;
        }

        // 첨부파일 정보 배열 생성 (JSON.stringify 하지 않음)
        const customerFileUrls = uploadedFiles;

        createQuotation({
            variables: {
                message: additionalRequest || null,
                items: quotationItems,
                customerFiles: customerFileUrls.length > 0 ? customerFileUrls : null
            }
        });
    };

    const allChecked = cartItems.length > 0 && cartItems.every(item => item.checked);
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const selectedItems = cartItems.filter(item => item.checked);

    // 로딩 상태
    if (loading) {
        return (
            <div className="cart-page">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>장바구니를 불러오는 중입니다...</div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className="cart-page">
                <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                    <div>장바구니를 불러오는 중 오류가 발생했습니다.</div>
                    <button onClick={() => window.location.reload()} style={{ marginTop: '20px', padding: '10px 20px' }}>
                        다시 시도
                    </button>
                </div>
            </div>
        );
    }

    // 로그인 체크 - early return
    if (!isAuthenticated()) {
        return (
            <div className="cart-page">
                <LoginPromptModal
                    isOpen={true}
                    onClose={() => {}}
                    onLogin={() => window.dispatchEvent(new CustomEvent('openLoginPanel'))}
                />
            </div>
        );
    }

    return (
        <div className="cart-page">
            <div className="cart-container">
                <div className="co-progress-indicator">
                    <div className="co-progress-line-bg"></div>
                    <div className={`co-progress-line-fill ${step >= 2 ? 'step2' : ''} ${step >= 3 ? 'step3' : ''}`}></div>
                    <div className={`step-item ${step >= 1 ? 'active' : ''}`}>
                        <div className={`step-dot ${step >= 1 ? 'active' : ''}`}></div>
                        <div className="step-label">견적서 만들기</div>
                    </div>
                    <div className={`step-item ${step >= 2 ? 'active' : ''}`}>
                        <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
                        <div className="step-label">입력한 내용 확인</div>
                    </div>
                    <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
                        <div className={`step-dot ${step >= 3 ? 'active' : ''}`}></div>
                        <div className="step-label">견적 요청 완료</div>
                    </div>
                </div>

                {step === 1 && (
                    <main className="cart-main-content">
                        <div className="quote-section">
                            <h2 className="quote-title">견적 요청</h2>
                            {cartItems.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '50px' }}>
                                    <div style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
                                        장바구니가 비어있습니다.
                                    </div>
                                    <button 
                                        onClick={() => navigate('/shop')}
                                        style={{ 
                                            padding: '10px 20px', 
                                            backgroundColor: '#007bff', 
                                            color: 'white', 
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        쇼핑 계속하기
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="cart-table">
                                        <div className="cart-table-header">
                                            <div className="header-cell checkbox">
                                                <input type="checkbox" checked={allChecked} onChange={handleCheckAll} />
                                            </div>
                                            <div className="header-cell product-info">제품 명</div>
                                            <div className="header-cell remarks">비고 (탭 or 기장)</div>
                                            <div className="header-cell quantity">수량</div>
                                            <div className="header-cell edit">편집</div>
                                        </div>
                                        {cartItems.map(item => (
                                            <div className="cart-table-row" key={item.id}>
                                                <div className="cell checkbox">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={item.checked} 
                                                        onChange={() => handleCheckItem(item.id)} 
                                                    />
                                                </div>
                                                <div className="cell product-info" data-label="제품 명">
                                                    <ImageWithPlaceholder 
                                                        src={item.imgSrc} 
                                                        alt={item.name} 
                                                        className="cart-item-image" 
                                                    />
                                                    <div className="item-details">
                                                        {item.code && item.code !== '코드 없음' && (
                                                            <p className="item-code">코드: {item.code}</p>
                                                        )}
                                                        <p className="item-name">{item.name}</p>
                                                        {item.size && item.size !== '미입력' && (
                                                            <p className="item-size">사이즈: {item.size}</p>
                                                        )}
                                                        {item.options && item.options.length > 0 && (
                                                            <p className="item-options">옵션: {item.options.join(', ')}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="cell remarks" data-label="비고 (탭 or 기장)">
                                                    {item.notes}
                                                </div>
                                                <div className="cell quantity" data-label="수량">
                                                    <div className="quantity-selector">
                                                        <button onClick={() => handleQuantityChange(item.id, -1)}>-</button>
                                                        <span>{item.quantity}</span>
                                                        <button onClick={() => handleQuantityChange(item.id, 1)}>+</button>
                                                    </div>
                                                </div>
                                                <div className="cell edit" data-label="편집">
                                                    <button 
                                                        className="delete-item-btn" 
                                                        onClick={() => deleteItem(item.id)}
                                                    >
                                                        목록에서 삭제
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button className="delete-selected-btn" onClick={deleteSelected}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7 21C6.45 21 5.979 20.804 5.587 20.412C5.195 20.02 5 19.549 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.804 20.021 18.412 20.413C18.02 20.805 17.549 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z" fill="white"/>
                                        </svg>
                                        선택한 목록 삭제
                                    </button>
                                </>
                            )}
                        </div>

                        {cartItems.length > 0 && (
                            <>
                                <div className="request-section">
                                    <h3 className="request-title">추가 요청사항</h3>
                                    <textarea 
                                        className="request-textarea" 
                                        placeholder="제품에 추가 Tap이나 Hole을 작업이 필요하거나, 납기 등의 추가사항이 있다면 적어주세요." 
                                        value={additionalRequest} 
                                        onChange={(e) => setAdditionalRequest(e.target.value)}
                                    />
                                </div>

                                <div className="file-upload-section">
                                    <h3 className="request-title">파일 첨부</h3>
                                    <p className="file-info">도면이나 참고자료가 있으시면 첨부해 주세요. (선택사항)</p>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                        id="file-upload-cart"
                                        accept=".dwg,.dxf,.step,.stp,.iges,.igs,.sat,.x_t,.x_b,.obj,.stl,.ply,.3ds,.fbx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif"
                                    />
                                    <button 
                                        className="file-upload-btn" 
                                        onClick={() => document.getElementById('file-upload-cart').click()}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? '업로드 중...' : '파일 첨부'}
                                    </button>
                                    
                                    {uploadedFiles.length > 0 && (
                                        <div className="uploaded-files">
                                            <h4>첨부된 파일:</h4>
                                            {uploadedFiles.map((file, index) => (
                                                <div key={index} className="uploaded-file-item">
                                                    <span className="file-name">{file.name}</span>
                                                    <span className="file-size">({(file.size / 1024).toFixed(1)}KB)</span>
                                                    <button 
                                                        className="remove-file-btn" 
                                                        onClick={() => handleRemoveFile(index)}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="action-buttons">
                                    <button className="back-btn" onClick={() => navigate(-1)}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="white"/>
                                        </svg>
                                        돌아가기
                                    </button>
                                    <button className="confirm-btn" onClick={handleNextStep}>
                                        확인
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="white"/>
                                        </svg>
                                    </button>
                                </div>
                            </>
                        )}
                    </main>
                )}

                {step === 2 && (
                    <main className="cart-confirm-content">
                        <div className="confirm-info-section">
                            <h2>견적 요청 확인</h2>
                            <p>등록한 이메일로 견적서가 발송됩니다.</p>
                            <p className="info-text">이메일 주소는 내 정보에서 변경할 수 있습니다.</p>
                        </div>

                        <div className="quote-section">
                            <h2 className="quote-title">견적 요청 상품</h2>
                            <div className="confirm-item-list">
                                {selectedItems.map(item => (
                                    <div className="confirm-item" key={item.id}>
                                        <ImageWithPlaceholder 
                                            src={item.imgSrc} 
                                            alt={item.name} 
                                            className="confirm-item-image" 
                                        />
                                        <div className="confirm-item-details">
                                            {item.code && item.code !== '코드 없음' && (
                                                <p className="item-code">코드: {item.code}</p>
                                            )}
                                            <p className="item-name">{item.name}</p>
                                            {item.size && item.size !== '미입력' && (
                                                <p className="item-size">사이즈: {item.size}</p>
                                            )}
                                            {item.options && item.options.length > 0 && (
                                                <p className="item-options">옵션: {item.options.join(', ')}</p>
                                            )}
                                        </div>
                                        <div className="confirm-item-notes">{item.notes}</div>
                                        <div className="confirm-item-quantity">{item.quantity}개</div>
                                    </div>
                                ))}
                            </div>
                            <div className="total-quantity-summary">
                                <span>총 주문 수량 :</span>
                                <span className="total-quantity">{selectedItems.reduce((sum, item) => sum + item.quantity, 0)} 개</span>
                            </div>
                        </div>

                        <div className="request-summary-section">
                            <h3 className="request-title">추가 요청사항</h3>
                            <p className="request-summary-text">{additionalRequest || '미입력'}</p>
                        </div>
                        
                        <div className="action-buttons">
                            <button className="back-btn" onClick={handlePrevStep}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="white"/>
                                </svg>
                                돌아가기
                            </button>
                            <button 
                                className="confirm-btn" 
                                onClick={handleCreateQuotation}
                                disabled={creatingQuotation}
                            >
                                {creatingQuotation ? '처리 중...' : '견적 요청'}
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="white"/>
                                </svg>
                            </button>
                        </div>
                    </main>
                )}
                
                {step === 3 && (
                    <main className="cart-complete-content">
                        <div className="complete-message-section">
                            <h2>견적 요청이 완료되었습니다!</h2>
                            <p>견적번호: {quotationNumber}</p>
                            <p>등록한 이메일로 견적서가 발송됩니다.</p>
                            <p>견적서는 관리자 확인 후 답변드리겠습니다.</p>
                        </div>
                        <div className="action-buttons">
                            <button className="back-btn" onClick={() => navigate('/shop')}>
                                쇼핑 계속하기
                            </button>
                            <button className="confirm-btn" onClick={() => navigate('/quotation-history')}>
                                나의 견적이력 보기
                            </button>
                        </div>
                    </main>
                )}
            </div>
        </div>
    );
};

export default CartPage; 